import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Terminal, Play, CheckCircle2, Clock, DollarSign, Zap, BookOpen, Code2, Brain, Shield, Rocket, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// Font loader component
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

    .font-display {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      font-feature-settings: 'ss01' on, 'ss02' on;
    }
    .font-space {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .font-jetbrains {
      font-family: 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace;
    }
  `}</style>
);

// Terminal typing effect hook
const useTypewriter = (text: string, speed: number = 50, startDelay: number = 0) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayText('');
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let i = 0;
      const timer = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);
      return () => clearInterval(timer);
    }, startDelay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, startDelay]);

  return { displayText, isComplete };
};

// Blinking cursor component
const Cursor = ({ visible = true }: { visible?: boolean }) => (
  <motion.span
    animate={{ opacity: visible ? [1, 0] : 0 }}
    transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
    className="inline-block w-2 h-5 bg-green-400 ml-0.5 align-middle"
  />
);

// Terminal window component
const TerminalWindow = ({
  children,
  title = "terminal",
  className = ""
}: {
  children: React.ReactNode;
  title?: string;
  className?: string;
}) => (
  <div className={`bg-[#0a0a0a] rounded-lg border border-[#333] overflow-hidden ${className}`}>
    {/* Title bar */}
    <div className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border-b border-[#333]">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
        <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
        <div className="w-3 h-3 rounded-full bg-[#27ca40]" />
      </div>
      <span className="text-xs text-gray-500 font-jetbrains ml-2">{title}</span>
    </div>
    {/* Content */}
    <div className="p-4 font-jetbrains text-sm">
      {children}
    </div>
  </div>
);

// Scanline overlay effect
const Scanlines = () => (
  <div
    className="pointer-events-none fixed inset-0 z-50 opacity-[0.03]"
    style={{
      background: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(0,255,0,0.03) 1px, rgba(0,255,0,0.03) 2px)'
    }}
  />
);

// Module data
const modules = [
  { week: 1, name: 'Tokens', desc: 'How LLMs see text', icon: '🔤' },
  { week: 1, name: 'LLM Calls', desc: 'Your first API call', icon: '📞' },
  { week: 1, name: 'Streaming', desc: 'Real-time responses', icon: '🌊' },
  { week: 1, name: 'Embeddings', desc: 'Semantic search', icon: '🧲' },
  { week: 1, name: 'Context & Memory', desc: 'Handling conversations', icon: '🧠' },
  { week: 1, name: 'Structured Outputs', desc: 'JSON, validation', icon: '📋' },
  { week: 2, name: 'Tool Calling', desc: 'Let LLMs use functions', icon: '🔧' },
  { week: 2, name: 'Prompt Engineering', desc: 'Write effective prompts', icon: '✍️' },
  { week: 2, name: 'RAG Basics', desc: 'Retrieval augmented generation', icon: '📚' },
  { week: 2, name: 'Chunking', desc: 'Document processing', icon: '✂️' },
  { week: 2, name: 'Observability', desc: 'Monitoring with Helicone', icon: '👁️' },
  { week: 2, name: 'Cost Optimization', desc: 'Caching, model selection', icon: '💰' },
  { week: 3, name: 'Evals & Testing', desc: 'Validate AI outputs', icon: '✅' },
  { week: 3, name: 'Agentic Patterns', desc: 'ReAct, planning, reflection', icon: '🤖' },
  { week: 3, name: 'Context Engineering', desc: 'Window optimization', icon: '🎯' },
  { week: 3, name: 'LLM Gateways', desc: 'Fallbacks, routing, retries', icon: '🚪' },
  { week: 3, name: 'Guardrails & Safety', desc: 'PII detection, moderation', icon: '🛡️' },
  { week: 4, name: 'Semantic Caching', desc: 'Similarity-based caching', icon: '💾' },
  { week: 4, name: 'Production Monitoring', desc: 'Logs, traces, costs', icon: '📊' },
  { week: 4, name: 'Capstone Project', desc: 'Ship your first AI feature', icon: '🚀' },
];

const AITerminalCourseStudy = () => {
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [terminalStep, setTerminalStep] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((scrolled / maxScroll) * 100);

      // Update active section based on scroll
      const sections = document.querySelectorAll('[data-section]');
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          setActiveSection(index);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Terminal animation
  useEffect(() => {
    const timer = setInterval(() => {
      setTerminalStep(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const terminalLines = [
    { cmd: '$ npx ai-terminal-course', output: null },
    { cmd: null, output: 'Loading Module 1: Tokens...' },
    { cmd: null, output: '' },
    { cmd: '> Type any sentence:', output: null },
    { cmd: 'Hello, world!', output: null, isInput: true },
    { cmd: null, output: '' },
    { cmd: null, output: '┌─────────────────────────────────┐' },
    { cmd: null, output: '│  Token  │  ID    │  Bytes       │' },
    { cmd: null, output: '├─────────────────────────────────┤' },
    { cmd: null, output: '│  Hello  │  9906  │  [72,101..]  │' },
    { cmd: null, output: '│  ,      │  11    │  [44]        │' },
    { cmd: null, output: '│   world │  1917  │  [32,119..]  │' },
    { cmd: null, output: '│  !      │  0     │  [33]        │' },
    { cmd: null, output: '└─────────────────────────────────┘' },
    { cmd: null, output: '' },
    { cmd: null, output: '💡 Notice: " world" (with space) is ONE token', isHighlight: true },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
      <FontLoader />
      <Scanlines />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-[#1a1a1a] z-50">
        <motion.div
          className="h-full bg-green-500"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/labs')}
            className="flex items-center gap-2 text-gray-500 hover:text-green-400 transition-colors font-jetbrains text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>cd ../labs</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-4"
          >
            <span className="text-gray-600 font-jetbrains text-xs hidden md:block">
              scroll: {Math.round(scrollProgress)}%
            </span>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} data-section className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Terminal prompt intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <span className="font-jetbrains text-green-500 text-sm">
              $ cat /story/how-i-learned-ai.md
            </span>
          </motion.div>

          {/* Main headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight"
          >
            I Watched{' '}
            <span className="text-red-400 line-through opacity-60">100 Hours</span>
            <br />
            of AI Tutorials.
            <br />
            <span className="text-green-400">Built Nothing.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-400 mb-12 font-jetbrains"
          >
            So I built a course that teaches differently.
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a
              href="https://www.npmjs.com/package/ai-terminal-course"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-green-500 hover:bg-green-400 text-black font-jetbrains font-bold rounded transition-all flex items-center gap-2"
            >
              <Terminal className="w-5 h-5" />
              npx ai-terminal-course
            </a>
            <div className="flex items-center gap-6 text-gray-500 font-jetbrains text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> 10 hours
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" /> ~$10 API cost
              </span>
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-gray-600 font-jetbrains text-xs"
            >
              ↓ scroll to read the story
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* The Problem Section */}
      <section data-section className="min-h-screen flex items-center px-6 py-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            {/* Left: The confession */}
            <div>
              <span className="font-jetbrains text-green-500 text-sm mb-4 block">
                // the_problem.js
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-medium text-white mb-6 tracking-tight">
                The Year I Tried to Learn AI
              </h2>
              <div className="space-y-4 text-gray-400">
                <p>
                  November 2022. ChatGPT launches. I'm a software engineer with 5 years of experience.
                  I tell myself: "I need to learn this."
                </p>
                <p>
                  There was too much new stuff. Tokens, embeddings, RAG, agents, prompts, context windows. Every week a new framework. Every day a new model.
                </p>
                <p className="text-white font-medium">
                  The harder part wasn't understanding demos. It was making things work reliably in production.
                </p>
                <p>
                  Tutorials showed happy paths. Real systems needed error handling, cost control, fallbacks, observability. Nobody taught that.
                </p>
              </div>
            </div>

            {/* Right: The cycle visualization */}
            <TerminalWindow title="tutorial_hell.log">
              <div className="space-y-2 text-gray-400">
                <p className="text-yellow-400"># My learning loop:</p>
                <p><span className="text-gray-600">1.</span> Watch 3-hour video course</p>
                <p><span className="text-gray-600">2.</span> Feel productive</p>
                <p><span className="text-gray-600">3.</span> Try to build something</p>
                <p><span className="text-gray-600">4.</span> Realize I forgot everything</p>
                <p><span className="text-gray-600">5.</span> Find another video course</p>
                <p><span className="text-gray-600">6.</span> <span className="text-red-400">goto 1</span></p>
                <p className="mt-4 text-gray-600"># Total: 100+ hours</p>
                <p className="text-gray-600"># Shipped: 0 projects</p>
                <p className="text-gray-600"># Money spent: $297</p>
                <p className="mt-4 text-red-400">ERROR: Learning != Watching</p>
              </div>
            </TerminalWindow>
          </motion.div>
        </div>
      </section>

      {/* The Insight Section */}
      <section data-section className="min-h-screen flex items-center px-6 py-24 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="font-jetbrains text-green-500 text-sm mb-4 block">
              // the_insight.js
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-white mb-12 tracking-tight">
              Then I Remembered Something
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* vimtutor story */}
              <div className="bg-[#111] rounded-lg p-6 border border-[#222]">
                <h3 className="font-space text-xl font-bold text-white mb-4">How I Learned Vim</h3>
                <p className="text-gray-400 mb-4">
                  Years ago, I wanted to learn Vim. Someone said: "Just run <code className="text-green-400 bg-[#1a1a1a] px-1 rounded">vimtutor</code>."
                </p>
                <p className="text-gray-400 mb-4">
                  30 minutes later, I could navigate and edit. No videos. No slides.
                  Just me, the terminal, and immediate feedback.
                </p>
                <p className="text-white font-medium">
                  The terminal forced me to DO, not watch.
                </p>
              </div>

              {/* The question */}
              <div className="bg-green-500/10 rounded-lg p-6 border border-green-500/30">
                <h3 className="font-space text-xl font-bold text-green-400 mb-4">The Question</h3>
                <p className="text-gray-300 text-lg mb-4">
                  "What if learning AI could feel like vimtutor?"
                </p>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>No video scrubbing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Type every command yourself</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>See real API responses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Muscle memory, not passive memory</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* The comparison */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-500/5 rounded-lg p-6 border border-red-500/20">
                <h4 className="text-red-400 font-jetbrains text-sm mb-4">// traditional_learning</h4>
                <ul className="space-y-2 text-gray-500">
                  <li>📺 Watch instructor code</li>
                  <li>📋 Copy-paste examples</li>
                  <li>🤔 "I'll remember this later"</li>
                  <li>❌ Forget in 2 days</li>
                  <li>🔁 Repeat</li>
                </ul>
              </div>
              <div className="bg-green-500/5 rounded-lg p-6 border border-green-500/20">
                <h4 className="text-green-400 font-jetbrains text-sm mb-4">// terminal_learning</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>⌨️ Type every command</li>
                  <li>👁️ See immediate output</li>
                  <li>🧠 Build muscle memory</li>
                  <li>💡 Understand by doing</li>
                  <li>🚀 Ship real features</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Experience Section */}
      <section data-section className="min-h-screen flex items-center px-6 py-24">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="font-jetbrains text-green-500 text-sm mb-4 block">
              // the_experience.js
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-white tracking-tight mb-4">
              What It Feels Like
            </h2>
            <p className="text-gray-400 mb-12 max-w-2xl">
              No browser tabs. No video player. Just you and the terminal.
              Every concept is learned by doing.
            </p>

            {/* Live terminal demo */}
            <TerminalWindow title="ai-terminal-course - Module 1: Tokens" className="max-w-3xl">
              <div className="space-y-1">
                {terminalLines.map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    {line.cmd && (
                      <p className={line.isInput ? 'text-white' : 'text-green-400'}>
                        {line.cmd}
                      </p>
                    )}
                    {line.output !== null && (
                      <p className={line.isHighlight ? 'text-yellow-400' : 'text-gray-400'}>
                        {line.output}
                      </p>
                    )}
                  </motion.div>
                ))}
                <div className="mt-4">
                  <span className="text-green-400">{'>'} </span>
                  <span className="text-white">Press Enter to continue...</span>
                  <Cursor />
                </div>
              </div>
            </TerminalWindow>

            {/* Key features */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="text-center p-6">
                <div className="text-4xl mb-4">⌨️</div>
                <h3 className="font-space text-white font-bold mb-2">Type Everything</h3>
                <p className="text-gray-500 text-sm">No copy-paste. Build muscle memory for AI patterns.</p>
              </div>
              <div className="text-center p-6">
                <div className="text-4xl mb-4">💸</div>
                <h3 className="font-space text-white font-bold mb-2">Real API Costs</h3>
                <p className="text-gray-500 text-sm">Spend $10 learning. Understand every dollar before production.</p>
              </div>
              <div className="text-center p-6">
                <div className="text-4xl mb-4">🧘</div>
                <h3 className="font-space text-white font-bold mb-2">Zero Distractions</h3>
                <p className="text-gray-500 text-sm">Terminal-only. No tabs, no notifications, no context switching.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Curriculum Section */}
      <section data-section className="min-h-screen px-6 py-24 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="font-jetbrains text-green-500 text-sm mb-4 block">
              $ ls ./modules/
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-white tracking-tight mb-4">
              20 Modules. 4 Weeks. $10.
            </h2>
            <p className="text-gray-400 mb-12">
              From "what's a token?" to shipping production AI features.
            </p>

            {/* Week grid */}
            <div className="space-y-8">
              {[1, 2, 3, 4].map(week => (
                <div key={week}>
                  <h3 className="font-jetbrains text-green-400 mb-4">
                    Week {week}: {
                      week === 1 ? 'Foundations' :
                      week === 2 ? 'Production Patterns' :
                      week === 3 ? 'Advanced' :
                      'Ship It'
                    }
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {modules.filter(m => m.week === week).map((module, i) => (
                      <motion.div
                        key={module.name}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className="bg-[#111] border border-[#222] rounded-lg p-3 hover:border-green-500/50 transition-colors group"
                      >
                        <div className="text-2xl mb-2">{module.icon}</div>
                        <h4 className="text-white text-sm font-medium mb-1 group-hover:text-green-400 transition-colors">
                          {module.name}
                        </h4>
                        <p className="text-gray-600 text-xs">{module.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section data-section className="min-h-screen flex items-center px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="font-jetbrains text-green-500 text-sm mb-4 block">
              // philosophy.md
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-white tracking-tight mb-12">
              The Manifesto
            </h2>

            <TerminalWindow title="LEARNING_PRINCIPLES.txt" className="mb-12">
              <div className="space-y-6 text-gray-300">
                <div>
                  <p className="text-green-400 font-bold">1. DOING {'>'} WATCHING</p>
                  <p className="text-gray-500 ml-4">
                    You can't learn to ride a bike by watching YouTube.
                    AI engineering is the same.
                  </p>
                </div>
                <div>
                  <p className="text-green-400 font-bold">2. REAL COSTS, REAL LEARNING</p>
                  <p className="text-gray-500 ml-4">
                    Spending your own money on API calls creates focus.
                    $10 in learning saves $1000 in production mistakes.
                  </p>
                </div>
                <div>
                  <p className="text-green-400 font-bold">3. THE TERMINAL IS A DOJO</p>
                  <p className="text-gray-500 ml-4">
                    No notifications. No tabs. No distractions.
                    Just you and the craft.
                  </p>
                </div>
                <div>
                  <p className="text-green-400 font-bold">4. MUSCLE MEMORY {'>'} MENTAL MEMORY</p>
                  <p className="text-gray-500 ml-4">
                    Type "client.chat.completions.create" 50 times.
                    You'll never forget it.
                  </p>
                </div>
                <div>
                  <p className="text-green-400 font-bold">5. SHIP, DON'T STUDY</p>
                  <p className="text-gray-500 ml-4">
                    The goal isn't to finish a course.
                    It's to ship AI features to real users.
                  </p>
                </div>
              </div>
            </TerminalWindow>

            {/* Quote */}
            <blockquote className="border-l-4 border-green-500 pl-6 py-4">
              <p className="text-2xl text-white italic mb-4">
                "The best way to learn AI is to make 1000 API calls with your own money."
              </p>
              <cite className="text-gray-500 font-jetbrains text-sm">
                - What I wish someone told me in 2023
              </cite>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For */}
      <section data-section className="py-24 px-6 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="font-jetbrains text-green-500 text-sm mb-4 block">
              $ whoami
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-white tracking-tight mb-12">
              Is This For You?
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-6">
                <h3 className="text-green-400 font-jetbrains mb-4">✓ Perfect if:</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="text-green-400">→</span>
                    You can code in any language
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400">→</span>
                    You're comfortable with the terminal
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400">→</span>
                    You have ZERO AI knowledge (even better)
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400">→</span>
                    You learn by doing, not watching
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400">→</span>
                    You want to ship AI features, not just understand them
                  </li>
                </ul>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-6">
                <h3 className="text-red-400 font-jetbrains mb-4">✗ Not for you if:</h3>
                <ul className="space-y-3 text-gray-500">
                  <li className="flex items-start gap-3">
                    <span className="text-red-400">→</span>
                    You prefer video content
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400">→</span>
                    You're looking for ML/deep learning theory
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400">→</span>
                    You want certificates for your LinkedIn
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400">→</span>
                    You're not comfortable spending $10 on learning
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-section className="min-h-[80vh] flex items-center justify-center px-6 py-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-jetbrains text-green-500 text-sm mb-6 block">
              $ echo "Ready?"
            </span>

            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white mb-6 tracking-tight">
              Stop Reading.
              <br />
              <span className="text-green-400">Start Building.</span>
            </h2>

            <p className="text-xl text-gray-400 mb-12">
              Your first API call is 5 minutes away.
            </p>

            <TerminalWindow title="getting-started" className="max-w-lg mx-auto mb-8 text-left">
              <div className="space-y-2">
                <p className="text-gray-500"># Start instantly (no install):</p>
                <p className="text-green-400">$ npx ai-terminal-course</p>
                <p className="text-gray-500 mt-4"># Or install globally:</p>
                <p className="text-green-400">$ npm install -g ai-terminal-course</p>
                <p className="text-green-400">$ ai-terminal</p>
              </div>
            </TerminalWindow>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.npmjs.com/package/ai-terminal-course"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-500 hover:bg-green-400 text-black font-jetbrains font-bold rounded transition-all flex items-center gap-2"
              >
                <Rocket className="w-5 h-5" />
                Start Learning
              </a>
              <a
                href="https://github.com/josharsh/ai-terminal-course"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-gray-600 hover:border-gray-400 text-gray-300 font-jetbrains rounded transition-all flex items-center gap-2"
              >
                <Code2 className="w-5 h-5" />
                View Source
              </a>
            </div>

            <p className="text-gray-600 font-jetbrains text-sm mt-8">
              MIT Licensed • Open Source • Free Forever
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222] py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 font-jetbrains text-sm">
            Built by{' '}
            <a href="https://twitter.com/AKJosh21" className="text-green-400 hover:underline">
              @josharsh
            </a>
          </p>
          <div className="flex items-center gap-6 text-gray-600 font-jetbrains text-sm">
            <a href="https://github.com/josharsh/ai-terminal-course" className="hover:text-green-400 transition-colors">
              GitHub
            </a>
            <a href="https://www.npmjs.com/package/ai-terminal-course" className="hover:text-green-400 transition-colors">
              npm
            </a>
          </div>
        </div>
      </footer>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="ai-terminal-course" variant="dark" />
    </div>
  );
};

export default AITerminalCourseStudy;
